import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { formatFullThaiDate } from '@krudream/shared'
import { apiUrl } from '@/lib/api'

type Status = 'present' | 'absent' | 'leave'

interface AttendanceRecord {
  studentId: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
  attendanceId: string | null
  status: Status | null
  note: string | null
}

export function AttendanceEditPage() {
  const { date } = useParams()
  const navigate = useNavigate()
  const [records, setRecords] = useState<AttendanceRecord[]>([])
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(null)
  const [newStatus, setNewStatus] = useState<Status>('present')
  const [reason, setReason] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  // Check if date is within 7-day edit window
  const dateObj = date ? new Date(date + 'T00:00:00') : new Date()
  const daysSince = Math.floor((Date.now() - dateObj.getTime()) / 86400000)
  const canEdit = daysSince <= 7 && daysSince >= 0

  useEffect(() => {
    if (!date) return
    fetch(apiUrl(`/api/attendance?date=${date}`), { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setRecords(data.data.students)
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [date])

  function selectStudent(r: AttendanceRecord) {
    setSelectedRecord(r)
    setNewStatus(r.status || 'present')
    setReason('')
  }

  async function handleSave() {
    if (!selectedRecord?.attendanceId || !reason.trim()) return
    setIsSubmitting(true)

    try {
      const res = await fetch(apiUrl(`/api/attendance/${selectedRecord.attendanceId}`), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          reason: reason.trim(),
        }),
      })
      const data = await res.json()
      if (data.success) {
        setRecords((prev) =>
          prev.map((r) =>
            r.studentId === selectedRecord.studentId
              ? { ...r, status: newStatus }
              : r,
          ),
        )
        setSelectedRecord(null)
        showToast('บันทึกการแก้ไขแล้ว')
      } else {
        showToast(data.error?.messageTh || 'เกิดข้อผิดพลาด')
      }
    } catch {
      showToast('เกิดข้อผิดพลาด')
    }
    setIsSubmitting(false)
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  const statusColors: Record<Status, string> = {
    present: 'text-green-600',
    absent: 'text-red-600',
    leave: 'text-amber-600',
  }

  const statusLabels: Record<Status, string> = {
    present: 'มา',
    absent: 'ขาด',
    leave: 'ลา',
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-400">กำลังโหลด...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate('/attendance')} className="text-gray-600">⟨</button>
          <h1 className="text-lg font-bold text-gray-900">แก้ไขการเช็คชื่อ</h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4">
        <p className="mb-3 text-sm text-gray-500">
          {formatFullThaiDate(dateObj)}
        </p>

        {!canEdit && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">
            ไม่สามารถแก้ไขข้อมูลเกิน 7 วันย้อนหลัง
          </div>
        )}

        {/* Student list */}
        {!selectedRecord ? (
          <div className="space-y-1">
            {records.filter((r) => r.status !== null).map((r) => (
              <button
                key={r.studentId}
                onClick={() => canEdit && selectStudent(r)}
                disabled={!canEdit}
                className="flex w-full items-center justify-between rounded-lg bg-white px-3 py-2.5 text-left text-sm shadow-sm disabled:opacity-50"
              >
                <span>
                  {r.studentNumber} · {r.prefix}{r.fullName}
                  {r.nickname && <span className="text-gray-400"> ({r.nickname})</span>}
                </span>
                <span className={`font-medium ${statusColors[r.status!]}`}>
                  {statusLabels[r.status!]}
                </span>
              </button>
            ))}
          </div>
        ) : (
          /* Edit form */
          <div className="space-y-4">
            <div className="rounded-lg bg-white p-4 shadow-sm">
              <p className="font-medium text-gray-900">
                {selectedRecord.studentNumber} · {selectedRecord.prefix}{selectedRecord.fullName}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                สถานะเดิม: <span className={`font-medium ${statusColors[selectedRecord.status!]}`}>
                  {statusLabels[selectedRecord.status!]}
                </span>
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">สถานะใหม่ *</label>
              <div className="flex overflow-hidden rounded-lg border border-gray-200">
                {(['present', 'absent', 'leave'] as Status[]).map((s) => {
                  const colors: Record<Status, string> = {
                    present: 'bg-green-500 text-white',
                    absent: 'bg-red-500 text-white',
                    leave: 'bg-amber-400 text-white',
                  }
                  return (
                    <button
                      key={s}
                      onClick={() => setNewStatus(s)}
                      className={`flex-1 py-3 text-sm font-medium transition-colors ${
                        newStatus === s ? colors[s] : 'bg-white text-gray-500'
                      }`}
                    >
                      {statusLabels[s]}
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                เหตุผลในการแก้ไข *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="เช่น ผู้ปกครองโทรแจ้งทีหลัง"
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none"
              />
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
              แก้ไขย้อนหลังได้ไม่เกิน 7 วัน — การแก้ไขจะถูกบันทึกใน audit log
            </div>

            <button
              onClick={handleSave}
              disabled={isSubmitting || !reason.trim()}
              className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
            <button
              onClick={() => setSelectedRecord(null)}
              className="w-full py-2 text-sm text-gray-500"
            >
              ยกเลิก
            </button>
          </div>
        )}
      </main>

      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="attendance" />
    </div>
  )
}
